import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CompareProductsComponent } from '@dialogs/compare-products/compare-products.component';
import { CreateProductDialogComponent } from '@dialogs/create-product-dialog/create-product-dialog.component';
import { InquiryDialogComponent } from '@dialogs/inquiry-dialog/inquiry-dialog.component';
import { ProductSearchDialogComponent } from '@dialogs/product-search-dialog/product-search-dialog.component';
import { AIValidationStatuses, AppToolItem, ColorAliases } from '@mds/common';
import { MdsIcon } from '@mds/icons';
import { SideBarOrganismsComponent } from '@mds/ui';
import { AnalyzeService } from '@services/analyze.service';
import { ReusableService } from '@services/reusable.service';
import { ToastrService } from '@services/toaster.service';
import { LayersAPIService } from '../../services/layers-api.service';
import { PerformanceAnalyticsComponent } from './sidepanel-performance-analytics/performance-analytics.component';

@Component({
  selector: 'app-side-panel',
  standalone: true,
  imports: [SideBarOrganismsComponent, PerformanceAnalyticsComponent],
  providers: [LayersAPIService],
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss']
})
export class SidePanelComponent implements OnChanges, OnInit {
  @Input() data: any;
  @Input() info: any;
  @Input() analyzeData: any;
  @Input() view: string;
  @Input() app: any;
  @Input() updatingAIStatus: boolean;
  productName: string;
  productType: string;
  @Input() selectionText = 'Selection on sankey';
  contextualTools: any;
  modelTools: any;
  alternatives: any[];
  chipContent: any;
  @Output() valueChanged = new EventEmitter<any>();
  @Output() showSidePanel = new EventEmitter<any>();
  @Output() onClickValidateAINode = new EventEmitter<void>();
  stage: string;
  isAnalyticsPanel = false;
  isAnalyticsPanelOpen = false;
  private readonly destroyRef = inject(DestroyRef);
  private layersAPIService = inject(LayersAPIService);
  private reusableService = inject(ReusableService);
  private toasterService = inject(ToastrService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private analyzeService = inject(AnalyzeService);
  private selectedProductInfo: any;

  whiteColor = [ColorAliases.INVERSE_CONTENT];
  headerIconColor = null;
  aiGenerated = false;
  aiValidationStatus: AIValidationStatuses;

  ngOnInit() {
    this.reusableService.productValidationChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) return;
      this.aiValidationStatus = value.validation_status;
    });
  }

  ngOnChanges() {
    if (!this.data) return;
    this.isAnalyticsPanel =
      window.location.pathname.startsWith('/mattermaps') &&
      !['overview', 'dpm'].includes(this.analyzeService.getLayer()) &&
      this.analyzeData?.analytics?.pin_deltas?.length > 0;
    this.isAnalyticsPanelOpen = this.isAnalyticsPanel;
    this.isAnalyticsPanelOpen = this.view !== 'sidepanel';
    this.contextualTools = this.data.contextualTools;
    this.reusableService.pradicateBy(this.contextualTools, 'sortlevel');
    this.modelTools = this.data?.modelTools?.map((modelTool: AppToolItem) => ({
      ...modelTool,
      icon: this.getIcon(modelTool)
    }));
    this.alternatives = this.data?.alternatives;
    this.chipContent = this.data.chipContent;
    this.selectedProductInfo = this.info?.[0];
    if (this.selectedProductInfo) {
      this.productName = this.selectedProductInfo.title;
      this.productType = this.selectedProductInfo.card_type_label;
      this.aiGenerated = this.selectedProductInfo.ai_generated;
      this.aiValidationStatus = this.selectedProductInfo.validation_status;
    }

    if (this.productType == 'Restricted Substance List') {
      this.headerIconColor = this.whiteColor;
    }
    this.stage = this.info?.[0]?.stage;
  }

  openTool(data: AppToolItem) {
    const { link } = data;
    if (!link) {
      return;
    }
    if (link.type === 'modal') {
      this.openModal(`${link.url}`);
    } else if (link.type === 'int' || link.type === 'ext') {
      this.openLink(data);
    } else if (link.type === 'action') {
      this.goTosubtitute();
    } else if (link.type === 'api') {
      this.composerApiPassthrough(data);
    }
  }

  openLink(data: AppToolItem) {
    const link = data.link.url;
    const substring = 'mattermaps';
    const eco = link.split('/');
    if (eco.includes('ecofee')) {
      const data = {
        type: 'ecofee',
        product_rid: eco[eco.length - 2],
        ecofee_rid: eco[eco.length - 1]
      };
      this.callReturnEvent(data);
    } else if (link.endsWith('to-do/new')) {
      this.openTaskDialog();
    } else if (link.endsWith('internal_enrich_sku')) {
      this.openFindPriceAndSupplies();
    } else if (link.indexOf(substring) !== -1) {
      this.router.navigate([`${link}`]);
    } else if (link.includes('enrich')) {
      this.openFindPriceAndSupplies();
    } else if (link.startsWith('composer/metadata/')) {
      const rid = link.split('composer/metadata/')[1];
      this.router.navigate([`mattermaps/overview/${rid}/edit`]);
    } else {
      this.router.navigate([`${link}`]);
    }
  }

  openTaskDialog() {
    const data = {
      title: 'New Task',
      type: 'ShowTask',
      submit_button_text: 'Add Task'
    };
    this.dialog.open(InquiryDialogComponent, {
      width: '700px',
      height: '90vh',
      data,
      panelClass: 'todo-model-container'
    });
  }

  openFindPriceAndSupplies() {
    const rid = window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1);
    const data = {
      multiple: true,
      selectOption: false,
      type: 'enrich-search',
      action: 'enrich-product-search',
      rid
    };

    this.dialog.open(ProductSearchDialogComponent, {
      data,
      panelClass: 'standard-dialog'
    });
  }

  openModal(link?: string) {
    let rid = '';
    if (window.location.pathname.includes('/mattermaps/overview/')) {
      const parts = window.location.pathname.split('/mattermaps/overview/')[1].split('/');
      rid = parts[0];
    } else {
      rid = window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1);
    }

    if (link === 'tableview') {
      this.layersAPIService.getTileCards(rid).subscribe((res) => {
        const data = {
          rid,
          searchModeFlag: false,
          product: res[0]
        };
        this.dialog.open(CompareProductsComponent, {
          panelClass: 'product-compare-side-panel',
          data
        });
      });
    } else {
      const type = link.split('-');
      const eco = link.split('/');
      if (eco.includes('ecofee')) {
        const data = {
          type: 'ecofee',
          product_rid: eco[eco.length - 2],
          ecofee_rid: eco[eco.length - 1]
        };
        this.callReturnEvent(data);
      } else {
        this.gotoCreateNew(type[1]);
      }
    }
  }

  gotoCreateNew(type) {
    this.dialog.open(CreateProductDialogComponent, {
      autoFocus: false,
      data: {
        name: type,
        newTab: true
      }
    });
  }

  goTosubtitute() {
    const data = {
      type: 'pins'
    };
    this.callReturnEvent(data);
  }

  callReturnEvent(values?: any) {
    this.valueChanged.emit({
      value: values
    });
  }

  composerApiPassthrough(l) {
    if (l) {
      const rid = window.location.pathname.substring(window.location.pathname.lastIndexOf('/') + 1);
      let apiEndpoint = l.link.url ? l.link.url : '';
      if (rid) {
        if (l.link.url.includes('<rid>')) {
          apiEndpoint = l.link.url.replace('<rid>', rid);
        } else if (l.link.url.includes('{0}')) {
          apiEndpoint = l.link.url.replace('{0}', rid);
        }
      }
      if (l.label === 'Recalculate downstream chains') {
        const disableAutoHide = 0;
        this.toasterService.showMessage('Recalculation in progress... Please wait.', disableAutoHide);
      }
      this.layersAPIService.apiPassthrough(apiEndpoint, l.link.method, l.link.payload).subscribe({
        next: (res) => {
          this.toasterService.removeMessage();
          if (res?.message) this.toasterService.showMessage(res.message, 5000);
          const data = {
            type: 'reload'
          };
          this.callReturnEvent(data);
        },
        error: () => {
          this.toasterService.removeMessage();
        }
      });
    }
  }

  checkToggle(flag) {
    this.showSidePanel.emit(flag);
  }

  private getIcon(modelTool: AppToolItem) {
    switch (modelTool.label) {
      case 'View Product Model':
        return MdsIcon.FRAME_INSPECT;
      case 'Explore Model':
        return MdsIcon.DYNAMIC_UPLOAD;
      default:
        return MdsIcon.FRAME_INSPECT;
    }
  }

  onValidateProduct() {
    this.onClickValidateAINode.emit();
  }
}
